import { Button } from '@fountain/fountain-ui-components';
import { CircularProgress, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { BlockedTooltip, viewerBlockedReason } from './blockedViewer';
import messages from './messages';
import { RejectOpeningDialog } from './RejectOpeningDialog';
import { useStyles } from './styles';
import { ApprovalRequest } from './types';
import { useRecordDecision } from './useApprovalRequests';

interface OpeningActionsCellProps {
  row: ApprovalRequest;
  /**
   * Held by something outside this row — today a bulk run that may already be
   * deciding it. Without this the cell's own mutation is the only thing that
   * disables it, and a second press posts a third decision for a level the bulk
   * run has already claimed.
   */
  disabled: boolean;
  /**
   * Re-requests the page, so the row shows what the decision did to it.
   * Resolves once the page has been re-read.
   */
  onDecided: () => Promise<void>;
  /**
   * Whether this row now has a decision of its own in flight.
   *
   * Reported upward because the row's checkbox is outside this cell: without it
   * the row stays selectable mid-decision, and a bulk run started over the top
   * posts a second decision for the level this one has already claimed. The
   * mirror of the hold a bulk run puts on these buttons.
   */
  onDecidingChange: (id: string, deciding: boolean) => void;
}

/**
 * Approve or reject an opening from its own row (Design Requirements §5).
 *
 * ## Why an opening decides inline and an offer does not
 *
 * An offer is a document somebody has to read before deciding, so its row opens
 * the review panel. An opening has none: the levels and the rule are already in
 * the two columns to the left, which is the whole of what there is to read. A
 * panel for it would be an empty frame around two buttons.
 *
 * ## One click decides one level
 *
 * `actionableStepIds[0]`, not all of them. A decision is recorded per step
 * (`POST .../decisions` takes one `step_id`), and a person named on two
 * concurrently-advancing rules holds two assignments on one opening (AXHE-4183).
 * Deciding both from one press would mean two requests whose second can fail
 * after the first has committed — so the press decides the level in front of it,
 * the page re-requests, and the row comes back still offering the buttons for the
 * second. The Approval status column is what shows the first one having flipped.
 *
 * ## Nothing rather than a status word
 *
 * When the viewer holds no open assignment the cell is empty. The row's state is
 * the Approval status column's job, and repeating it under an "Actions" heading
 * would read as a control that does nothing.
 */
export const OpeningActionsCell: React.FC<OpeningActionsCellProps> = ({
  row,
  disabled,
  onDecided,
  onDecidingChange,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // From the landed write until the page has re-read this row. The page keeps
  // its rows on screen through that read, and until it lands this row still
  // offers the level it has just decided: a second press would post a decision
  // the server has already recorded.
  const [rereading, setRereading] = useState(false);

  const { mutation, result } = useRecordDecision(row.id, {
    onSuccess: () => {
      setRejecting(false);
      setError(undefined);
      setRereading(true);
      onDecided().finally(() => setRereading(false));
    },
    // The server's own sentence where there is one, and a generic line where
    // there is not: `useApiServiceMutation` only extracts a body from an
    // `ApiError`, so a network failure or a 500 with no JSON arrives as
    // `undefined` — and a press that produces no visible answer reads as a
    // control that did nothing.
    //
    // The fallback cannot fire on a cancel: the hook returns on `CancelError`
    // before it reaches `onError`. That is what makes a generic line safe here
    // rather than something that would accuse the page of failing every time a
    // request was abandoned.
    onError: body =>
      setError(body?.message ?? intl.formatMessage(messages.decisionFailed)),
  });

  const stepId = row.actionableStepIds[0];
  const deciding = result.status === 'loading' || rereading;
  const submitting = deciding || disabled;
  // Shown disabled with the reason, as the offer footer does: the server names
  // the level, and the decisions endpoint refuses an approver without the
  // permission.
  const blockedReason = viewerBlockedReason(row, intl);

  // Reported from an effect rather than at the press, so the page learns about
  // the end of a decision as well as the start. The cleanup reports `false` so a
  // row the next response drops cannot leave its id marked as deciding forever.
  useEffect(() => {
    onDecidingChange(row.id, deciding);
    return () => onDecidingChange(row.id, false);
  }, [deciding, row.id, onDecidingChange]);
  const openingName = row.subject.positionName;

  if (stepId === undefined) return null;

  return (
    <>
      <div className={styles.rowActions}>
        {/* Reject first and outlined-negative, Approve last and filled. A
            rejection short-circuits the whole chain, so it is the most
            consequential control here and the least prominent by design. */}
        <BlockedTooltip reason={blockedReason}>
          <Button
            autoWidth
            negative
            className={styles.destructiveButton}
            size="small"
            type="secondary"
            // Every row's controls read "Reject" and "Approve", so the accessible
            // name carries the opening — otherwise a screen reader user hears the
            // same two names once per row with nothing telling them apart.
            aria-label={intl.formatMessage(messages.rejectOpeningFor, {
              opening: openingName ?? '',
            })}
            disabled={submitting || blockedReason !== undefined}
            onClick={() => {
              setError(undefined);
              setRejecting(true);
            }}
          >
            <FormattedMessage {...globalMessages.reject} />
          </Button>
        </BlockedTooltip>
        <BlockedTooltip reason={blockedReason}>
          <Button
            autoWidth
            size="small"
            type="primary"
            // Named explicitly because the label is swapped for a spinner while
            // the decision is in flight, which would otherwise leave the control
            // with no accessible name at the moment somebody is waiting to hear
            // what happened.
            aria-label={intl.formatMessage(messages.approveOpeningFor, {
              opening: openingName ?? '',
            })}
            disabled={submitting || blockedReason !== undefined}
            onClick={() => {
              setError(undefined);
              mutation(stepId, 'approved');
            }}
          >
            {submitting ? (
              <CircularProgress size={16} />
            ) : (
              <FormattedMessage {...globalMessages.approve} />
            )}
          </Button>
        </BlockedTooltip>
      </div>

      {/* An approval refused after the press. The dialog carries its own copy of
          this for a rejection, which is where a rejection's failure is read. */}
      {error && !rejecting && (
        <Typography className={styles.rowActionError} role="alert" variant="body2">
          {error}
        </Typography>
      )}

      {rejecting && (
        <RejectOpeningDialog
          count={1}
          error={error}
          holdsEdit={row.holdsEdit}
          openingName={openingName}
          submitting={submitting}
          onCancel={() => {
            setRejecting(false);
            setError(undefined);
          }}
          onConfirm={reason => {
            setError(undefined);
            mutation(stepId, 'rejected', reason);
          }}
        />
      )}
    </>
  );
};
